import * as Sentry from "@sentry/tanstackstart-react";
import { StartClient } from "@tanstack/react-start/client";
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import posthog from "posthog-js";
import { hydrateRoot } from "react-dom/client";
import { initReactI18next } from "react-i18next";
import * as z from "zod";

import { APP_ENV } from "src/appEnv";
import { i18nextOptions, localeCookieName } from "src/lib/i18n/i18nextOptions";
import { initSentry } from "src/lib/sentry/initSentry.mjs";

import { env } from "./env";
import { I18nPackageBackendClient } from "./lib/i18n/I18nPackageBackend";
import { isomorphicRequestDataRef } from "./lib/isomorphicRequestData/isomorphicRequestDataRef";

initSentry({
  release: import.meta.env.VITE_APP_VERSION ?? "dev",
  environment: APP_ENV,
  integrations: [Sentry.replayIntegration()],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

async function loadLocale(locale: string) {
  const { default: setupLocale } = await import(
    `../node_modules/zod/v4/locales/${locale}.js`
  );
  z.config(setupLocale());
}

if (!i18next.isInitialized) {
  i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(I18nPackageBackendClient)
    .init({
      ...i18nextOptions,
      partialBundledLanguages: true,
      detection: {
        order: ["querystring", "cookie", "htmlTag", "navigator"],
        lookupCookie: localeCookieName,
        caches: ["cookie"],
      },
    })
    .then(async () => {
      await loadLocale(i18next.language);
      // then hydrate your app wrapped in the I18nextProvider
      return hydrateRoot(document, <StartClient />);
    });
}

const timezoneOffset = new Date().getTimezoneOffset();
document.cookie = `timezone-offset=${timezoneOffset}; path=/; max-age=31536000`;

posthog.init(env.PUBLIC_POSTHOG_KEY, {
  api_host: env.POSTHOG_HOST,
  person_profiles: "always",
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
  },
  bootstrap: (() => {
    return isomorphicRequestDataRef.current?.posthogBootstrap ?? {};
  })(),
});
